import ReportToolbarButton from '../shared/ReportToolbarButton';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import Chart from 'react-apexcharts';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Skeleton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tab,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { alpha, ThemeProvider } from '@mui/material/styles';
import { MdFilterAlt, MdInfoOutline, MdShowChart } from 'react-icons/md';
import { AppVarsContext } from '../../context/AppVarsContext';
import CampaignSelectionFilter, { normalizeCampaignOptions } from '../shared/campaignSelectionFilter';
import { formatReportDateRangeLabel } from '../shared/reportStatusCards';
import {
  buildInfillionRequest,
  formatCurrency,
  formatDecimal,
  formatInteger,
  formatPercent,
  getConfiguredAdvertiserIds,
  normalizeReportResponse,
  readApiError,
} from './report-data';

import { createReportSurfaceTheme, getReportSurfaceSx, reportColors, styleReportChart, getReportPageSx, getAnalyticsReportTabsSx } from '../shared/report-design';

import ResponsiveTabs from '../../system/responsive-tabs';
import ReportPerformanceOverview, { ReportSectionIntro } from '../shared/ReportPerformanceOverview';

const INFILLION_CHART_COLORS = [reportColors.revenue, reportColors.conversions];

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(`${String(value).slice(0, 10)}T00:00:00.000Z`);
  return Number.isNaN(date.getTime())
    ? String(value)
    : new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }).format(date);
};

const reportErrorCopy = (error) => {
  if (error?.code === 'AUTHENTICATION_REQUIRED' || error?.status === 401) {
    return { title: 'Your reporting session has expired', detail: 'Sign in again, then refresh this report.' };
  }
  if (error?.code === 'CLIENT_SCOPE_FORBIDDEN' || error?.status === 403) {
    return { title: 'This report is outside your client access', detail: 'Open an assigned client report or contact your administrator.' };
  }
  if (error?.code === 'INFILLION_NOT_CONFIGURED') {
    return { title: 'Choose at least one programmatic advertiser', detail: 'An administrator can assign imported advertisers in the report profile.' };
  }
  const detail = String(error?.message || 'Refresh the report or try again later.').replace(/\bInfillion\b/gi, 'Programmatic');
  return { title: 'Programmatic performance is temporarily unavailable', detail };
};

const CoverageInfo = ({ data, status, statusError }) => {
  const [open, setOpen] = useState(false);
  const theme = useTheme();
  const coverage = data?.coverage;
  const requestedDays = Number(coverage?.requestedDays || 0);
  const coveredDays = Number(coverage?.coveredDays || 0);
  const coveragePercent = requestedDays ? Math.min(100, (coveredDays / requestedDays) * 100) : 0;
  const state = coverage?.status || (status?.reportingReady ? 'ready' : 'not-ready');
  const tone = state === 'complete' ? 'success' : state === 'partial' ? 'warning' : 'info';
  const title = state === 'complete'
    ? 'The selected range is fully covered'
    : state === 'partial'
      ? `${coveredDays} of ${requestedDays} days contain imported delivery data`
      : state === 'empty'
        ? 'No imported activity overlaps this date range'
        : status?.configured
          ? 'Imported performance data is not ready yet'
          : 'Programmatic advertiser scope is not configured';

  return (
    <Tooltip
      describeChild
      arrow
      placement="bottom-end"
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      disableTouchListener
      slotProps={{ tooltip: { sx: { ...getReportSurfaceSx(theme), maxWidth: 380, p: 2, color: 'text.primary' } } }}
      title={
        <Box sx={{ display: 'grid', gap: 1.25 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{title}</Typography>
          <Typography variant="body2" color="text.secondary">
            {coverage?.availableStart
              ? `Imported window ${formatDate(coverage.availableStart)} - ${formatDate(coverage.availableEnd)} · ${formatInteger(coverage.rowCount)} delivery rows`
              : status?.latestImport
                ? `Latest import covers ${formatDate(status.latestImport.rangeStart)} - ${formatDate(status.latestImport.rangeEnd)}`
                : 'Import status is available to administrators in client settings.'}
          </Typography>
          {requestedDays ? <Typography variant="caption" color="text.secondary">{coveredDays} of {requestedDays} requested days covered</Typography> : null}
          {requestedDays ? (
            <Box role="img" aria-label={`${coveredDays} of ${requestedDays} requested days covered`} sx={{ height: 8, borderRadius: 99, overflow: 'hidden', bgcolor: theme.palette.divider }}>
              <Box sx={{ width: `${coveragePercent}%`, height: '100%', background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)` }} />
            </Box>
          ) : null}
          {state === 'partial' ? <Alert severity={tone}>Totals include available rows only. Uncovered days are not displayed as zero-performance days.</Alert> : null}
          {statusError ? <Alert severity="warning">Import status could not be refreshed, but available performance results are still shown.</Alert> : null}
        </Box>
      }
    >
      <ReportToolbarButton
        startIcon={<MdInfoOutline />}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onClick={() => setOpen(true)}
        aria-label={`Data coverage: ${title}`}
      >
        Data coverage
      </ReportToolbarButton>
    </Tooltip>
  );
};

const SectionHeading = ({ eyebrow, title, detail }) => (
  <Box sx={{ display: 'grid', gap: 0.4 }}>
    <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: '0.14em' }}>{eyebrow}</Typography>
    <Typography component="h2" variant="h6" sx={{ fontWeight: 700 }}>{title}</Typography>
    {detail ? <Typography variant="body2" color="text.secondary">{detail}</Typography> : null}
  </Box>
);

const DetailTable = ({ type, rows, truncated }) => {
  const theme = useTheme();
  const stacked = useMediaQuery(theme.breakpoints.down('md'));
  const isCreative = type === 'creative';
  const visible = rows.slice(0, 50);
  return (
    <Card elevation={0} sx={{ overflow: 'hidden' }}>
      <CardContent sx={{ p: { xs: 2, md: 2.5 }, display: 'grid', gap: 2 }}>
        <SectionHeading
          eyebrow={isCreative ? 'Creative detail' : 'Campaign detail'}
          title={isCreative ? 'Messages driving delivery' : 'Campaign performance'}
          detail={`${formatInteger(rows.length)} ${isCreative ? 'creatives' : 'campaigns'} in the selected scope${rows.length > visible.length ? ' · first 50 shown' : ''}`}
        />
        {stacked ? <Box role="list" aria-label={`Programmatic ${type} performance`}>
          {visible.map((row, index) => <Box component="article" role="listitem" key={index} sx={{ py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography sx={{ fontWeight: 700, overflowWrap: 'anywhere' }}>{isCreative ? row.creativeName : row.campaignName}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, overflowWrap: 'anywhere' }}>{isCreative ? row.campaignName : row.advertiserName}</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1 }}>
              {[
                ['Impressions', formatInteger(row.impressions)], ['Clicks', formatInteger(row.clicks)], ['CTR', formatPercent(row.ctr)],
                ['Conversions', formatDecimal(row.conversions)], ['Spend', formatCurrency(row.spend)]
              ].map(([label, value]) => <Box key={label}><Typography variant="caption" color="text.secondary">{label}</Typography><Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>{value}</Typography></Box>)}
            </Box>
          </Box>)}
        </Box> : (
        <TableContainer sx={{ maxWidth: '100%', '@media print': { overflow: 'visible' } }}>
          <Table size="small" aria-label={`Programmatic ${type} performance`} sx={{ minWidth: { xs: 720, md: 0 }, '@media print': { minWidth: 0 } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '38%' }}>{isCreative ? 'Creative' : 'Campaign'}</TableCell>
                <TableCell sx={{ width: '24%' }}>{isCreative ? 'Campaign' : 'Advertiser'}</TableCell>
                <TableCell align="right">Impressions</TableCell>
                <TableCell data-report-metric="click" align="right">Clicks</TableCell>
                <TableCell data-report-metric="click" align="right">CTR</TableCell>
                <TableCell data-report-metric="conversion" align="right">Conversions</TableCell>
                <TableCell align="right">Spend</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.map((row) => (
                <TableRow key={`${isCreative ? row.creativeId : row.campaignId}-${row.advertiserId || ''}`} hover>
                  <TableCell sx={{ whiteSpace: 'normal', overflowWrap: 'anywhere', fontWeight: 700 }}>
                    {isCreative ? row.creativeName : row.campaignName}
                    <Typography component="span" display="block" variant="caption" color="text.secondary">{isCreative ? row.creativeId : row.campaignId}</Typography>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }}>{isCreative ? row.campaignName : row.advertiserName}</TableCell>
                  <TableCell align="right">{formatInteger(row.impressions)}</TableCell>
                  <TableCell data-report-metric="click" align="right">{formatInteger(row.clicks)}</TableCell>
                  <TableCell data-report-metric="click" align="right">{formatPercent(row.ctr)}</TableCell>
                  <TableCell data-report-metric="conversion" align="right">{formatDecimal(row.conversions)}</TableCell>
                  <TableCell align="right">{formatCurrency(row.spend)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>)}
        {truncated ? <Alert severity="info">The API returned the highest-spend groups within its response limit. Narrow the date range or advertiser scope for complete detail.</Alert> : null}
      </CardContent>
    </Card>
  );
};

export default function InfillionReport({ setLoading: setParentLoading, reportProfile, title = 'Programmatic', dataOverride = null, statusOverride = null, stateOverride = '' }) {
  const brandTheme = useTheme();
  const theme = useMemo(() => createReportSurfaceTheme(brandTheme), [brandTheme]);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { appVars } = useContext(AppVarsContext);
  const cid = sessionStorage.getItem('rid') || '';
  const [range, setRange] = useState(() => ({ startDate: sessionStorage.getItem('sDate') || '', endDate: sessionStorage.getItem('eDate') || '' }));
  const [data, setData] = useState(() => (stateOverride === 'loading' ? null : (dataOverride ? normalizeReportResponse(dataOverride) : null)));
  const [status, setStatus] = useState(statusOverride);
  const [error, setError] = useState(null);
  const [statusError, setStatusError] = useState(null);
  const [loading, setLoading] = useState(stateOverride === 'loading');
  const [refreshKey, setRefreshKey] = useState(0);
  const [tabIndex, setTabIndex] = useState(0);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selectedCampaignIds, setSelectedCampaignIds] = useState(null);
  const [draftCampaignIds, setDraftCampaignIds] = useState([]);
  const requestId = useRef(0);
  const advertiserIds = useMemo(() => getConfiguredAdvertiserIds(reportProfile), [reportProfile]);
  const publicReportSlug = sessionStorage.getItem('reportSlug') || '';

  useEffect(() => {
    const handleRange = (event) => setRange({
      startDate: event?.detail?.sDate || sessionStorage.getItem('sDate') || '',
      endDate: event?.detail?.eDate || sessionStorage.getItem('eDate') || '',
    });
    window.addEventListener('dc', handleRange);
    return () => window.removeEventListener('dc', handleRange);
  }, []);

  const loadReport = useCallback(async (activeRequest) => {
    setLoading(true);
    setParentLoading?.(true);
    setError(null);
    setStatusError(null);
    setData(null);

    const statusPromise = fetch(`${appVars.api_url}/infillion/status?cid=${encodeURIComponent(cid)}`, {
      method: 'GET', credentials: 'include',
      headers: publicReportSlug ? { 'X-Report-Slug': publicReportSlug } : undefined,
    }).then(async (response) => {
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw readApiError(payload, response.status);
      return payload;
    });
    const reportPromise = fetch(`${appVars.api_url}/infillion/report`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...(publicReportSlug ? { 'X-Report-Slug': publicReportSlug } : {}) },
      body: JSON.stringify(buildInfillionRequest({ cid, ...range, advertiserIds, selectedCampaignIds })),
    }).then(async (response) => {
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw readApiError(payload, response.status);
      return normalizeReportResponse(payload);
    });

    const [statusResult, reportResult] = await Promise.allSettled([statusPromise, reportPromise]);
    if (requestId.current !== activeRequest) return;
    if (statusResult.status === 'fulfilled') setStatus(statusResult.value);
    else setStatusError(statusResult.reason);
    if (reportResult.status === 'fulfilled') setData(reportResult.value);
    else setError(reportResult.reason);
    setLoading(false);
    setParentLoading?.(false);
  }, [advertiserIds, appVars?.api_url, cid, publicReportSlug, range, selectedCampaignIds, setParentLoading]);

  useEffect(() => {
    if (dataOverride || stateOverride) {
      setStatus(statusOverride);
      if (stateOverride === 'loading') setData(null);
      if (stateOverride === 'error') setError({ code: 'INFILLION_REPORT_FAILED', message: 'The deterministic visual test returned an API error.' });
      else if (stateOverride !== 'loading') {
        const next = normalizeReportResponse(dataOverride || {});
        if (stateOverride === 'empty') next.coverage = { ...next.coverage, status: 'empty', coveredDays: 0, rowCount: 0 };
        if (stateOverride === 'empty') Object.assign(next, { daily: [], advertisers: [], campaigns: [], creatives: [] });
        if (stateOverride === 'partial') next.coverage = { ...next.coverage, status: 'partial', coveredDays: Math.max(1, next.coverage.requestedDays - 2) };
        setData(next);
        setLoading(false);
      }
      setParentLoading?.(stateOverride === 'loading');
      return undefined;
    }
    if (!appVars?.api_url || !cid || !range.startDate || !range.endDate) return undefined;
    const activeRequest = requestId.current + 1;
    requestId.current = activeRequest;
    loadReport(activeRequest).catch((loadError) => {
      if (requestId.current !== activeRequest) return;
      setError(readApiError({}, loadError?.status || 500));
      setLoading(false);
      setParentLoading?.(false);
    });
    return () => { requestId.current += 1; };
  }, [appVars?.api_url, cid, dataOverride, loadReport, range.endDate, range.startDate, refreshKey, setParentLoading, stateOverride, statusOverride]);

  const dailyOptions = useMemo(() => styleReportChart(theme, {
    chart: { type: 'line', toolbar: { show: false }, zoom: { enabled: false } },
    colors: INFILLION_CHART_COLORS,
    dataLabels: { enabled: false },
    stroke: { curve: 'smooth', width: [3, 2] },
    fill: { type: 'solid', opacity: 1 },
    xaxis: { categories: (data?.daily || []).map((point) => formatDate(point.date)), labels: { rotate: isMobile ? -45 : 0, hideOverlappingLabels: true } },
    yaxis: [
      { title: { text: 'Spend' }, labels: { formatter: (value) => `$${formatInteger(value)}` } },
      { opposite: true, title: { text: 'Impressions' }, labels: { formatter: (value) => formatInteger(value) } },
    ],
    legend: { position: 'top', horizontalAlign: 'left' },
    grid: { borderColor: alpha(theme.palette.text.primary, 0.09) },
    tooltip: { shared: true, y: [{ formatter: (value) => formatCurrency(value) }, { formatter: (value) => formatInteger(value) }] },
  }), [data?.daily, isMobile, theme]);
  const advertiserOptions = useMemo(() => styleReportChart(theme, {
    chart: { type: 'bar', toolbar: { show: false }, fontFamily: theme.typography.fontFamily },
    colors: [reportColors.revenue],
    dataLabels: { enabled: false },
    plotOptions: { bar: { horizontal: true, borderRadius: 5, barHeight: '52%' } },
    xaxis: { categories: (data?.advertisers || []).map((item) => item.advertiserName), labels: { formatter: (value) => `$${formatInteger(value)}` } },
    yaxis: { labels: { maxWidth: isMobile ? 120 : 260 } },
    grid: { borderColor: alpha(theme.palette.text.primary, 0.09) },
    tooltip: { y: { formatter: (value) => formatCurrency(value) } },
  }), [data?.advertisers, isMobile, theme]);

  const topAdvertiser = [...(data?.advertisers || [])].sort((a, b) => Number(b.spend || 0) - Number(a.spend || 0))[0];
  const topCampaign = [...(data?.campaigns || [])].sort((a, b) => Number(b.conversions || 0) - Number(a.conversions || 0))[0];



  const errorCopy = reportErrorCopy(error);
  const isEmpty = data?.coverage?.status === 'empty';
  const scopeLabel = data?.selectedAdvertisers?.map((item) => item.advertiserName).join(', ') || `${advertiserIds.length} saved advertiser${advertiserIds.length === 1 ? '' : 's'}`;
  const rangeLabel = formatReportDateRangeLabel(range.startDate, range.endDate);
  const campaignOptions = normalizeCampaignOptions(data?.availableCampaigns);

  return (
    <ThemeProvider theme={theme}><Box sx={getReportPageSx(theme)}>
      {loading && <LinearProgress aria-label="Loading programmatic report" />}

      <Box sx={{ ...getReportSurfaceSx(theme), p: 1, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
        <ResponsiveTabs value={tabIndex} onChange={(_, value) => setTabIndex(value)} aria-label="Programmatic report pages" variant="scrollable" scrollButtons="auto" sx={{ ...getAnalyticsReportTabsSx(), flex: '1 1 300px' }}>
          {['Performance', 'Campaigns', 'Creatives'].map((label, index) => <Tab key={label} label={label} id={`programmatic-tab-${index}`} aria-controls={`programmatic-panel-${index}`} />)}
        </ResponsiveTabs>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, '@media print': { display: 'none' } }}>
            <CoverageInfo data={data} status={status} statusError={statusError} />
            <ReportToolbarButton
              startIcon={<MdFilterAlt />}
              onClick={() => {
                setDraftCampaignIds(Array.isArray(selectedCampaignIds) ? selectedCampaignIds : campaignOptions.map((option) => option.id));
                setFiltersOpen(true);
              }}
              disabled={!campaignOptions.length && loading}
            >
              Filter
            </ReportToolbarButton>
          </Box>      </Box>

      {tabIndex > 0 ? <ReportSectionIntro report="Programmatic" title={tabIndex === 1 ? 'Campaign performance' : 'Creative performance'} dateLabel={`${scopeLabel} · ${rangeLabel}`} description={tabIndex === 1 ? 'Compare campaign delivery, response, and attributed actions.' : 'Inspect the messages driving display delivery and response.'} /> : null}

      {Array.isArray(selectedCampaignIds) ? (
        <Alert severity={selectedCampaignIds.length ? 'info' : 'warning'}>
          {selectedCampaignIds.length
            ? `${selectedCampaignIds.length} of ${campaignOptions.length} campaigns are included in every report section.`
            : 'No campaigns are selected. Open Filter to choose campaigns.'}
        </Alert>
      ) : null}

      <Dialog open={filtersOpen} onClose={() => setFiltersOpen(false)} fullScreen={isMobile} fullWidth maxWidth="sm">
        <DialogTitle>Report filters</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <CampaignSelectionFilter options={campaignOptions} selectedIds={draftCampaignIds} onChange={setDraftCampaignIds} loading={loading} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFiltersOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              const allSelected = campaignOptions.length > 0 && draftCampaignIds.length === campaignOptions.length;
              setSelectedCampaignIds(allSelected ? null : [...draftCampaignIds]);
              setFiltersOpen(false);
            }}
          >
            Apply filters
          </Button>
        </DialogActions>
      </Dialog>


      {error ? (
        <Alert
          severity={error?.status === 401 || error?.status === 403 ? 'warning' : 'error'}
          action={<Button color="inherit" size="small" onClick={() => setRefreshKey((value) => value + 1)}>Try again</Button>}
        >
          <Typography component="div" sx={{ fontWeight: 800 }}>{errorCopy.title}</Typography>
          {errorCopy.detail}
        </Alert>
      ) : null}

      {isEmpty ? (
        <Card elevation={0} sx={{ border: `1px dashed ${alpha(theme.palette.primary.main, 0.26)}` }}>
          <CardContent sx={{ py: { xs: 5, md: 7 }, textAlign: 'center', display: 'grid', gap: 1, justifyItems: 'center' }}>
            <MdShowChart size={42} color={theme.palette.primary.main} />
            <Typography variant="h5" sx={{ fontWeight: 800 }}>No programmatic activity in this range</Typography>
            <Typography color="text.secondary" sx={{ maxWidth: 620 }}>Choose dates inside the imported coverage window or ask an administrator to import a newer delivery batch.</Typography>
          </CardContent>
        </Card>
      ) : null}

      {loading && !data && <Box role="status" aria-label="Loading programmatic content" sx={{ display: 'grid', gap: 2 }}>
        <Card><CardContent><Skeleton width="24%" /><Skeleton width="52%" height={40} /><Skeleton width="70%" /></CardContent></Card>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2 }}>
          {[0, 1, 2, 3].map(item => <Card key={item}><CardContent><Skeleton width="60%" /><Skeleton height={44} /><Skeleton width="45%" /></CardContent></Card>)}
        </Box>
        <Card><CardContent><Skeleton variant="rounded" height={300} /></CardContent></Card>
      </Box>}
      {data && !isEmpty ? (
        <>
          {tabIndex === 0 ? <Box role="tabpanel" id="programmatic-panel-0" aria-labelledby="programmatic-tab-0" sx={{ display: 'grid', gap: 2, minWidth: 0 }}>
          <ReportPerformanceOverview eyebrow="Programmatic" title={`${title} Performance`}
            description={`See how display visibility becomes traffic and attributed actions during ${rangeLabel}.`}
            badge={scopeLabel}
            stages={[
              { label: 'Acquisition', description: 'Display delivery across the saved advertiser scope', metrics: [
                { label: 'Impressions', value: formatInteger(data.summary.impressions), detail: 'Ads served' },
                { label: 'Advertisers', value: formatInteger(data.advertisers.length), detail: 'Advertisers with activity' }
              ] },
              { label: 'Response', description: 'Traffic earned from display exposure', metrics: [
                { label: 'Clicks', value: formatInteger(data.summary.clicks), detail: 'Traffic generated' },
                { label: 'CTR', value: formatPercent(data.summary.ctr), detail: 'Click-through rate' }
              ] },
              { label: 'Outcome', description: 'Attributed actions and media investment', metrics: [
                { label: 'Conversions', value: formatDecimal(data.summary.conversions), detail: 'Attributed actions' },
                { label: 'Spend', value: formatCurrency(data.summary.spend), detail: 'Media delivered' }
              ] }
            ]}
            supportMetrics={[{ label: 'Campaigns', value: formatInteger(data.campaigns.length) }, { label: 'Creatives', value: formatInteger(data.creatives.length) }]}
            insight={topAdvertiser ? `${topAdvertiser.advertiserName} leads advertiser spend at ${formatCurrency(topAdvertiser.spend)}. Compare delivery and attributed actions in the campaign detail.` : 'No advertiser activity was returned for this range.'}
            highlights={[
              { label: 'Top advertiser by spend', title: topAdvertiser?.advertiserName || 'No advertiser data', value: formatCurrency(topAdvertiser?.spend), detail: 'Media investment in the selected range.' },
              { label: 'Top campaign by conversions', title: topCampaign?.campaignName || 'No campaign data', value: formatDecimal(topCampaign?.conversions), detail: 'Attributed actions from the leading campaign.' },
              { label: 'Delivery scope', title: 'Imported campaign activity', value: formatInteger(data.campaigns.length), detail: `${formatInteger(data.creatives.length)} creative groups in the selected scope.` }
            ]}
          />

          <Card elevation={0} sx={{ overflow: 'hidden', breakInside: 'avoid' }}>
            <CardContent sx={{ p: { xs: 2, md: 2.5 }, display: 'grid', gap: 2 }}>
              <SectionHeading eyebrow="Daily signal" title="Delivery and spend over time" detail="Spend and impressions are plotted on separate labeled scales." />
              <Chart
                type="line"
                height={isMobile ? 300 : 340}
                options={dailyOptions}
                series={[
                  { name: 'Spend', type: 'line', data: data.daily.map((point) => Number(point.spend || 0)) },
                  { name: 'Impressions', type: 'line', data: data.daily.map((point) => Number(point.impressions || 0)) },
                ]}
              />
            </CardContent>
          </Card>

          <Card elevation={0} sx={{ overflow: 'hidden', breakInside: 'avoid' }}>
            <CardContent sx={{ p: { xs: 2, md: 2.5 }, display: 'grid', gap: 2 }}>
              <SectionHeading eyebrow="Advertiser comparison" title="Spend by advertiser" detail="The active saved advertiser scope is ordered by spend." />
              <Chart
                type="bar"
                height={Math.max(220, data.advertisers.length * 58)}
                options={advertiserOptions}
                series={[{ name: 'Spend', data: data.advertisers.map((item) => Number(item.spend || 0)) }]}
              />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {data.selectedAdvertisers.map((item) => <Chip key={item.advertiserId} label={item.advertiserName} variant="outlined" sx={{ maxWidth: '100%', '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }} />)}
              </Box>
            </CardContent>
          </Card>

          </Box> : null}
          {tabIndex === 1 ? <Box role="tabpanel" id="programmatic-panel-1" aria-labelledby="programmatic-tab-1" sx={{ minWidth: 0 }}><DetailTable type="campaign" rows={data.campaigns} truncated={data.coverage.campaignsTruncated} /></Box> : null}
          {tabIndex === 2 ? <Box role="tabpanel" id="programmatic-panel-2" aria-labelledby="programmatic-tab-2" sx={{ minWidth: 0 }}><DetailTable type="creative" rows={data.creatives} truncated={data.coverage.creativesTruncated} /></Box> : null}
        </>
      ) : null}
    </Box></ThemeProvider>
  );
}
